import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { supabase, isSupabaseConfigured } from '../lib/supabase';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export default function ProtectedRoute({ children }: ProtectedRouteProps) {
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const location = useLocation();

  const hasTrigger = location.state?.authorizedTrigger === true;
  const hasAuthTokenKey = typeof window !== 'undefined' && Object.keys(localStorage).some(key => key.startsWith('sb-') && key.endsWith('-auth-token'));

  // If no secret trigger and no active session token, silently redirect to homepage immediately with zero loading states
  if (!hasTrigger && !hasAuthTokenKey) {
    return <Navigate to="/" replace />;
  }

  useEffect(() => {
    async function verifyAuth() {
      try {
        if (isSupabaseConfigured() && supabase) {
          const { data: { session } } = await supabase.auth.getSession();
          if (session && session.user) {
            // Query profiles table to check is_admin
            const { data: profile, error } = await supabase
              .from('profiles')
              .select('is_admin')
              .eq('id', session.user.id)
              .maybeSingle();

            if (!error && profile && profile.is_admin === true) {
              setIsAuthenticated(true);
            } else {
              setIsAuthenticated(false);
            }
          } else {
            setIsAuthenticated(false);
          }
        } else {
          setIsAuthenticated(false);
        }
      } catch (err) {
        console.error('Error verifying admin authorization:', err);
        setIsAuthenticated(false);
      } finally {
        setLoading(false);
      }
    }
    verifyAuth();
  }, []);

  // Inactivity session timer (60 minutes = 3600000 ms)
  useEffect(() => {
    if (!isAuthenticated) return;

    const INACTIVITY_TIMEOUT = 60 * 60 * 1000; // 60 minutes in milliseconds
    let lastActivity = Date.now();
    let timeoutId: any;

    const checkInactivity = async () => {
      const now = Date.now();
      if (now - lastActivity >= INACTIVITY_TIMEOUT) {
        console.warn('Admin session timed out due to 60 minutes of inactivity.');
        if (isSupabaseConfigured() && supabase) {
          await supabase.auth.signOut();
        }
        setIsAuthenticated(false);
        window.location.reload(); // Hard reload to clear authorization state and clear tokens
      } else {
        const remaining = INACTIVITY_TIMEOUT - (now - lastActivity);
        timeoutId = setTimeout(checkInactivity, Math.max(1000, remaining));
      }
    };

    const updateActivity = () => {
      lastActivity = Date.now();
    };

    // Human behavior tracking events
    window.addEventListener('mousemove', updateActivity);
    window.addEventListener('mousedown', updateActivity);
    window.addEventListener('keydown', updateActivity);
    window.addEventListener('scroll', updateActivity);
    window.addEventListener('touchstart', updateActivity);

    // Schedule checking
    timeoutId = setTimeout(checkInactivity, INACTIVITY_TIMEOUT);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('mousemove', updateActivity);
      window.removeEventListener('mousedown', updateActivity);
      window.removeEventListener('keydown', updateActivity);
      window.removeEventListener('scroll', updateActivity);
      window.removeEventListener('touchstart', updateActivity);
    };
  }, [isAuthenticated]);

  if (loading) {
    return (
      <div id="protected-route-loading" className="min-h-screen bg-slate-900 flex items-center justify-center text-white">
        <div className="text-center space-y-3">
          <div className="h-8 w-8 animate-spin rounded-full border-t-2 border-[#082C78] mx-auto" />
          <p className="text-xs font-bold uppercase tracking-widest text-slate-400">Verifying Admin Access Credentials...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    // Redirect to the dedicated administrative login gate if authorizedTrigger exists
    if (location.state?.authorizedTrigger === true) {
      return <Navigate to="/admin/login" state={{ from: location, authorizedTrigger: true }} replace />;
    }
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}
